/*
  Glass Hub: the live mirror.
  One outline engine (shapes as points, in inches) and one 2D renderer, shared by the home page hero,
  the product customiser and the 3D view (which builds its model from the same points).
  Units: 1 = one inch. y grows downwards (canvas), the 3D module flips it.
*/
(function () {
  'use strict';

  var N = 192; // points per outline; the same count for every shape so shapes can morph into each other
  var TAU = Math.PI * 2;

  /* ------------------------------------------------------------ outlines */

  function sdRoundBox(px, py, a, b, r) {
    var qx = Math.abs(px) - (a - r);
    var qy = Math.abs(py) - (b - r);
    var ox = Math.max(qx, 0);
    var oy = Math.max(qy, 0);
    return Math.sqrt(ox * ox + oy * oy) + Math.min(Math.max(qx, qy), 0) - r;
  }

  // Corner radius for each kind; capsule = half the short side, round and oval are ellipses.
  function radiusFor(kind, w, h, r) {
    if (kind === 'capsule') return Math.min(w, h) / 2;
    var safe = Math.max(0, Math.min(r == null ? 0.25 : r, Math.min(w, h) / 2));
    return safe;
  }

  function isEllipse(kind) {
    return kind === 'round' || kind === 'oval';
  }

  // Points of the outline, sampled by angle from the centre, so point i of every shape faces the same way.
  function outline(kind, w, h, r) {
    var a = Math.max(w, 0.01) / 2;
    var b = Math.max(h, 0.01) / 2;
    var pts = new Array(N);
    var ellipse = isEllipse(kind);
    var rr = ellipse ? 0 : radiusFor(kind, w, h, r);
    for (var i = 0; i < N; i++) {
      var t = (i / N) * TAU - Math.PI / 2;
      var c = Math.cos(t);
      var s = Math.sin(t);
      var d;
      if (ellipse) {
        d = 1 / Math.sqrt((c * c) / (a * a) + (s * s) / (b * b));
      } else {
        var lo = 0;
        var hi = Math.sqrt(a * a + b * b) + 1;
        for (var k = 0; k < 28; k++) {
          var mid = (lo + hi) / 2;
          if (sdRoundBox(c * mid, s * mid, a, b, rr) > 0) hi = mid;
          else lo = mid;
        }
        d = lo;
      }
      pts[i] = [c * d, s * d];
    }
    return pts;
  }

  // The same shape drawn smaller by `by` inches on every side (the inside edge of a print border).
  function inset(kind, w, h, r, by) {
    var iw = Math.max(w - by * 2, 0.5);
    var ih = Math.max(h - by * 2, 0.5);
    var ir = isEllipse(kind) ? 0 : Math.max(radiusFor(kind, w, h, r) - by, 0);
    return outline(kind === 'capsule' ? 'capsule' : kind, iw, ih, ir);
  }

  function lerpPoints(from, to, t) {
    var out = new Array(N);
    for (var i = 0; i < N; i++) out[i] = [from[i][0] + (to[i][0] - from[i][0]) * t, from[i][1] + (to[i][1] - from[i][1]) * t];
    return out;
  }

  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /* ------------------------------------------------------------- reading */

  // "Round", "Oval LED", "Capsule" … to a shape kind. Unknown text gives the fallback.
  function kindFrom(text, fallback) {
    var v = String(text || '').toLowerCase();
    if (/round|circle|circular/.test(v)) return 'round';
    if (/oval|ellip/.test(v)) return 'oval';
    if (/capsule|pill/.test(v)) return 'capsule';
    if (/square/.test(v)) return 'square';
    if (/rect/.test(v)) return 'rectangle';
    return fallback || 'rectangle';
  }

  // "24 x 36 in", "24×36", "Ø 30 in", "30 inch" → {w, h}; "Custom up to 6 sq ft" → {band: 6}.
  function sizeFrom(text, marker) {
    var v = String(text || '').toLowerCase().replace(/,/g, '.');
    var mark = String(marker || 'sq ft').toLowerCase();
    if (mark && v.indexOf(mark) !== -1) {
      var nums = v.match(/\d+(\.\d+)?/g);
      if (nums && nums.length) return { band: parseFloat(nums[nums.length - 1]), bandFrom: nums.length > 1 ? parseFloat(nums[0]) : 0 };
    }
    var pair = v.match(/(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")?\s*[x×*]\s*(\d+(?:\.\d+)?)/);
    if (pair) return { w: parseFloat(pair[1]), h: parseFloat(pair[2]) };
    var one = v.match(/(\d+(?:\.\d+)?)/);
    if (one) return { w: parseFloat(one[1]), h: parseFloat(one[1]) };
    return null;
  }

  /* --------------------------------------------------------- 2D renderer */

  var TONES = { warm: '#FFC978', neutral: '#FFE7BF', cool: '#E4F1FF' };

  function noisePattern(ctx) {
    var c = document.createElement('canvas');
    c.width = c.height = 96;
    var x = c.getContext('2d');
    var img = x.createImageData(96, 96);
    for (var i = 0; i < img.data.length; i += 4) {
      var n = 225 + Math.random() * 30;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = n;
      img.data[i + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    return ctx.createPattern(c, 'repeat');
  }

  function Mirror(canvas, opts) {
    opts = opts || {};
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.state = {
      kind: opts.kind || 'rectangle',
      w: opts.w || 24,
      h: opts.h || 36,
      r: opts.r == null ? 0.25 : opts.r,
      border: opts.border || 0, // print border width (inches)
      printArea: opts.printArea || 'border', // border | bands | full
      frostTop: opts.frostTop || 0,
      frostBottom: opts.frostBottom || 0,
      led: !!opts.led,
      lit: opts.lit == null ? !!opts.led : !!opts.lit,
      tone: opts.tone || 'warm',
      frame: opts.frame || null, // {width, depth, color, wood}
      dims: opts.dims !== false,
      fitTo: opts.fitTo || null, // {w, h}: the largest size, so smaller sizes look smaller
      editing: false,
      design: null, // {img, x, y, scale, rot}
      placeholder: opts.placeholder || '',
      unit: opts.unit || { inch: 'in', cm: 'cm', dia: 'Ø' },
      colors: opts.colors || {},
    };
    this.points = outline(this.state.kind, this.state.w, this.state.h, this.state.r);
    this.shown = { w: this.state.w, h: this.state.h, lit: this.state.lit ? 1 : 0 };
    this.anim = null;
    this.listeners = [];
    var self = this;
    this.resize();
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(function () {
        self.resize();
        self.draw();
      });
      this.ro.observe(canvas);
    }
    this.draw();
  }

  Mirror.outline = outline;
  Mirror.inset = inset;
  Mirror.kindFrom = kindFrom;
  Mirror.sizeFrom = sizeFrom;
  Mirror.tones = TONES;
  Mirror.N = N;

  Mirror.prototype.resize = function () {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var rect = this.canvas.getBoundingClientRect();
    var w = Math.max(rect.width, 50);
    var h = Math.max(rect.height, 50);
    this.cssW = w;
    this.cssH = h;
    this.dpr = dpr;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
  };

  Mirror.prototype.reduced = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // Change any part of the state; shape and size changes ease from the old outline to the new one.
  Mirror.prototype.set = function (next) {
    var s = this.state;
    var shapeChanged = false;
    for (var key in next) {
      if (!Object.prototype.hasOwnProperty.call(next, key)) continue;
      if ((key === 'kind' || key === 'w' || key === 'h' || key === 'r') && next[key] !== s[key]) shapeChanged = true;
      s[key] = next[key];
    }
    var target = outline(s.kind, s.w, s.h, s.r);
    var targetLit = s.led && s.lit ? 1 : 0;
    if (this.reduced() || !shapeChanged) {
      if (shapeChanged) {
        this.points = target;
        this.shown.w = s.w;
        this.shown.h = s.h;
      }
      this.fadeLit(targetLit);
      this.draw();
      this.emit();
      return;
    }
    var from = this.points;
    var fromW = this.shown.w;
    var fromH = this.shown.h;
    var start = performance.now();
    var self = this;
    var dur = 520;
    if (this.anim) cancelAnimationFrame(this.anim);
    var step = function (now) {
      var t = Math.min((now - start) / dur, 1);
      var e = ease(t);
      self.points = lerpPoints(from, target, e);
      self.shown.w = fromW + (s.w - fromW) * e;
      self.shown.h = fromH + (s.h - fromH) * e;
      self.draw();
      if (t < 1) self.anim = requestAnimationFrame(step);
      else {
        self.anim = null;
        self.points = target;
        self.emit();
      }
    };
    this.anim = requestAnimationFrame(step);
    this.fadeLit(targetLit);
  };

  Mirror.prototype.fadeLit = function (target) {
    var self = this;
    if (this.reduced()) {
      this.shown.lit = target;
      return;
    }
    if (Math.abs(this.shown.lit - target) < 0.01) return;
    var from = this.shown.lit;
    var start = performance.now();
    if (this.litAnim) cancelAnimationFrame(this.litAnim);
    var step = function (now) {
      var t = Math.min((now - start) / 600, 1);
      self.shown.lit = from + (target - from) * ease(t);
      if (!self.anim) self.draw();
      if (t < 1) self.litAnim = requestAnimationFrame(step);
      else self.litAnim = null;
    };
    this.litAnim = requestAnimationFrame(step);
  };

  Mirror.prototype.onChange = function (fn) {
    this.listeners.push(fn);
  };
  Mirror.prototype.emit = function () {
    for (var i = 0; i < this.listeners.length; i++) this.listeners[i](this);
  };

  // Pixels per inch and the centre, so the mirror, its frame, halo and labels fit the canvas.
  Mirror.prototype.layout = function (cw, ch, withLabels, ignoreFit) {
    var s = this.state;
    var fw = s.frame ? s.frame.width : 0;
    // bigger sizes are drawn bigger, but a small size still fills most of the space
    var ref = (!ignoreFit && s.fitTo) || { w: this.shown.w, h: this.shown.h };
    var refW = Math.max(ref.w * 0.72, this.shown.w) + fw * 2;
    var refH = Math.max(ref.h * 0.72, this.shown.h) + fw * 2;
    var pad = withLabels ? 64 : 28;
    var k = Math.min((cw - pad * 2) / refW, (ch - pad * 2) / refH);
    return { k: Math.max(k, 0.5), cx: cw / 2 + (withLabels ? -10 : 0), cy: ch / 2 + (withLabels ? 8 : 0) };
  };

  function tracePoints(ctx, pts, k, cx, cy, reverse) {
    var n = pts.length;
    for (var j = 0; j < n; j++) {
      var p = pts[reverse ? n - 1 - j : j];
      var x = cx + p[0] * k;
      var y = cy + p[1] * k;
      if (j === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
  }

  function scaledPoints(pts, sx, sy) {
    var out = new Array(pts.length);
    for (var i = 0; i < pts.length; i++) out[i] = [pts[i][0] * sx, pts[i][1] * sy];
    return out;
  }

  // The print / frosting / etching layer, drawn into any context at any scale.
  // Used for the screen, for the order preview PNG and as the 3D model's texture.
  Mirror.prototype.paintPrint = function (ctx, k, cx, cy, pts, w, h, opts) {
    opts = opts || {};
    var s = this.state;
    var c = s.colors;
    var frostTop = s.frostTop;
    var frostBottom = s.frostBottom;

    // frosting bands (Trending mirrors): white, slightly grainy
    if (frostTop || frostBottom) {
      ctx.save();
      ctx.beginPath();
      tracePoints(ctx, pts, k, cx, cy);
      ctx.clip();
      if (!this.noise) this.noise = noisePattern(ctx);
      ctx.globalAlpha = 0.86;
      ctx.fillStyle = this.noise;
      var top = cy - (h / 2) * k;
      var bottom = cy + (h / 2) * k;
      if (frostTop) ctx.fillRect(cx - (w / 2) * k - 2, top - 2, w * k + 4, frostTop * k + 2);
      if (frostBottom) ctx.fillRect(cx - (w / 2) * k - 2, bottom - frostBottom * k, w * k + 4, frostBottom * k + 2);
      ctx.restore();
    }

    if (s.printArea === 'none' || (!s.border && s.printArea === 'border')) return;

    // the area that carries the design
    ctx.save();
    ctx.beginPath();
    if (s.printArea === 'full') {
      tracePoints(ctx, pts, k, cx, cy);
    } else if (s.printArea === 'bands') {
      ctx.rect(cx - (w / 2) * k, cy - (h / 2) * k, w * k, frostTop * k);
      ctx.rect(cx - (w / 2) * k, cy + (h / 2) * k - frostBottom * k, w * k, frostBottom * k);
    } else {
      tracePoints(ctx, pts, k, cx, cy);
      var inner = inset(s.kind, w, h, s.r, s.border);
      tracePoints(ctx, inner, k, cx, cy, true);
    }
    ctx.clip('evenodd');
    if (s.printArea === 'bands') {
      ctx.beginPath();
      tracePoints(ctx, pts, k, cx, cy);
      ctx.clip();
    }

    var d = s.design;
    if (d && d.img && d.img.complete && d.img.naturalWidth) {
      var iw = d.img.naturalWidth;
      var ih = d.img.naturalHeight;
      var cover = Math.max((w * k) / iw, (h * k) / ih);
      var sc = cover * (d.scale || 1);
      ctx.translate(cx + (d.x || 0) * k, cy + (d.y || 0) * k);
      ctx.rotate(((d.rot || 0) * Math.PI) / 180);
      ctx.drawImage(d.img, (-iw * sc) / 2, (-ih * sc) / 2, iw * sc, ih * sc);
    } else if (!opts.clean) {
      // nothing chosen yet: a soft hatch so the customer sees where the design goes
      ctx.fillStyle = c.band || 'rgba(47,111,98,.10)';
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.strokeStyle = c.hatch || 'rgba(47,111,98,.22)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      var step = 10;
      for (var x = -ctx.canvas.height; x < ctx.canvas.width; x += step) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x + ctx.canvas.height, ctx.canvas.height);
      }
      ctx.stroke();
    }
    ctx.restore();
  };

  // Draw everything. `target` lets the same picture be drawn into another canvas (order preview).
  Mirror.prototype.draw = function (target) {
    var s = this.state;
    var ctx = target ? target.getContext('2d') : this.ctx;
    var dpr = target ? 1 : this.dpr;
    var cw = target ? target.width : this.cssW;
    var ch = target ? target.height : this.cssH;
    var clean = !!target;
    var c = s.colors;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);

    var showLabels = s.dims && !clean;
    var L = this.layout(cw, ch, showLabels, clean);
    var k = L.k;
    var cx = L.cx;
    var cy = L.cy;
    var w = this.shown.w;
    var h = this.shown.h;
    var pts = this.points;
    var lit = s.led ? this.shown.lit : 0;
    var tone = TONES[s.tone] || TONES.warm;

    // wall
    if (!clean || target.dataset.wall === '1') {
      var wall = ctx.createRadialGradient(cw / 2, ch * 0.42, 10, cw / 2, ch / 2, Math.max(cw, ch) * 0.75);
      wall.addColorStop(0, c.wallLight || '#FFFFFF');
      wall.addColorStop(1, c.wall || '#EEF0EF');
      ctx.fillStyle = wall;
      ctx.fillRect(0, 0, cw, ch);
    }

    // soft shadow on the wall
    ctx.save();
    ctx.beginPath();
    tracePoints(ctx, s.frame ? scaledPoints(pts, (w + s.frame.width * 2) / w, (h + s.frame.width * 2) / h) : pts, k, cx, cy + 6);
    ctx.shadowColor = 'rgba(21,24,27,.18)';
    ctx.shadowBlur = 26;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = 'rgba(21,24,27,.12)';
    ctx.fill();
    ctx.restore();

    // LED halo behind the glass: the outline filled with light and blurred, so only a soft
    // glow shows around the edge once the glass is drawn on top
    if (lit > 0.01) {
      ctx.save();
      ctx.globalAlpha = lit;
      var reach = Math.max(18, Math.min(cw, ch) * 0.07);
      ctx.beginPath();
      tracePoints(ctx, pts, k, cx, cy);
      ctx.fillStyle = tone;
      ctx.shadowColor = tone;
      ctx.shadowBlur = reach * 2.2;
      ctx.fill();
      ctx.shadowBlur = reach;
      ctx.fill();
      ctx.globalAlpha = lit * 0.6;
      ctx.shadowBlur = reach * 0.4;
      ctx.fill();
      ctx.restore();
    }

    // frame (aluminium or wood) around the glass
    if (s.frame && s.frame.width) {
      var fp = scaledPoints(pts, (w + s.frame.width * 2) / w, (h + s.frame.width * 2) / h);
      ctx.save();
      ctx.beginPath();
      tracePoints(ctx, fp, k, cx, cy);
      var fg = ctx.createLinearGradient(cx - (w / 2) * k, cy - (h / 2) * k, cx + (w / 2) * k, cy + (h / 2) * k);
      var fc = s.frame.color || '#B9BEC2';
      fg.addColorStop(0, shade(fc, 0.35));
      fg.addColorStop(0.45, fc);
      fg.addColorStop(0.55, shade(fc, -0.18));
      fg.addColorStop(1, shade(fc, 0.15));
      ctx.fillStyle = fg;
      ctx.fill();
      ctx.restore();
    }

    // the glass itself: silver with a soft sheen
    ctx.save();
    ctx.beginPath();
    tracePoints(ctx, pts, k, cx, cy);
    ctx.clip();
    var x0 = cx - (w / 2) * k;
    var y0 = cy - (h / 2) * k;
    var g = ctx.createLinearGradient(x0, y0, x0 + w * k, y0 + h * k);
    g.addColorStop(0, c.silverLight || '#F7FAFA');
    g.addColorStop(0.35, c.silver || '#D5DDDF');
    g.addColorStop(0.62, c.silverLight || '#EEF3F3');
    g.addColorStop(1, c.silverDark || '#BCC7CA');
    ctx.fillStyle = g;
    ctx.fillRect(x0 - 2, y0 - 2, w * k + 4, h * k + 4);
    // a faint reflection of the room: two soft vertical bands
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#9AA6AA';
    ctx.fillRect(x0 + w * k * 0.12, y0, w * k * 0.06, h * k);
    ctx.fillRect(x0 + w * k * 0.71, y0, w * k * 0.03, h * k);
    ctx.globalAlpha = 1;
    // diagonal sheen
    var sh = ctx.createLinearGradient(x0, y0, x0 + w * k, y0 + h * k * 0.6);
    sh.addColorStop(0.28, 'rgba(255,255,255,0)');
    sh.addColorStop(0.4, 'rgba(255,255,255,.55)');
    sh.addColorStop(0.47, 'rgba(255,255,255,0)');
    ctx.fillStyle = sh;
    ctx.fillRect(x0, y0, w * k, h * k);
    // warm wash on the glass while the light is on
    if (lit > 0.01) {
      ctx.globalAlpha = 0.16 * lit;
      ctx.fillStyle = tone;
      ctx.fillRect(x0, y0, w * k, h * k);
      ctx.globalAlpha = 1;
    }
    ctx.restore();

    // design, frosting, etching
    this.paintPrint(ctx, k, cx, cy, pts, w, h, { clean: clean });

    // LED line just inside the edge (front-lit look)
    if (lit > 0.01) {
      ctx.save();
      ctx.globalAlpha = 0.9 * lit;
      ctx.beginPath();
      var inner = scaledPoints(pts, 1 - 1.2 / w, 1 - 1.2 / h);
      tracePoints(ctx, inner, k, cx, cy);
      ctx.strokeStyle = tone;
      ctx.lineWidth = Math.max(2, 0.35 * k);
      ctx.shadowColor = tone;
      ctx.shadowBlur = 14;
      ctx.stroke();
      ctx.restore();
    }

    // the ground edge: pale green, like real 5mm mirror glass
    ctx.save();
    ctx.beginPath();
    tracePoints(ctx, pts, k, cx, cy);
    ctx.strokeStyle = c.edge || '#BFD8D0';
    ctx.lineWidth = Math.max(2, 0.18 * k);
    ctx.stroke();
    ctx.beginPath();
    tracePoints(ctx, scaledPoints(pts, 1 - 0.3 / w, 1 - 0.3 / h), k, cx, cy);
    ctx.strokeStyle = 'rgba(255,255,255,.8)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // while editing: the print-safe lines
    if (s.editing && !clean && s.printArea === 'border' && s.border) {
      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = c.guide || '#2F6F62';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      tracePoints(ctx, inset(s.kind, w, h, s.r, s.border), k, cx, cy);
      ctx.stroke();
      ctx.restore();
    }

    if (showLabels) this.drawLabels(ctx, k, cx, cy, w, h);
  };

  Mirror.prototype.drawLabels = function (ctx, k, cx, cy, w, h) {
    var s = this.state;
    var c = s.colors;
    var u = s.unit;
    var fw = s.frame ? s.frame.width : 0;
    var left = cx - (w / 2 + fw) * k;
    var right = cx + (w / 2 + fw) * k;
    var top = cy - (h / 2 + fw) * k;
    var bottom = cy + (h / 2 + fw) * k;
    var fmt = function (inch) {
      var r = Math.round(inch * 10) / 10;
      return r + ' ' + u.inch + ' (' + Math.round(inch * 2.54) + ' ' + u.cm + ')';
    };
    ctx.save();
    ctx.strokeStyle = c.label || '#5B6168';
    ctx.fillStyle = c.label || '#5B6168';
    ctx.lineWidth = 1;
    ctx.font = '500 12px ' + (c.font || 'Inter, system-ui, sans-serif');
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    var ellipseRound = s.kind === 'round';
    // width, above
    var ty = top - 16;
    line(ctx, left, ty, right, ty);
    tick(ctx, left, ty, true);
    tick(ctx, right, ty, true);
    pill(ctx, cx, ty, (ellipseRound ? u.dia + ' ' : '') + fmt(w + fw * 2), c);
    if (!ellipseRound) {
      // height, at the right
      var tx = right + 18;
      line(ctx, tx, top, tx, bottom);
      tick(ctx, tx, top, false);
      tick(ctx, tx, bottom, false);
      ctx.save();
      ctx.translate(tx, cy);
      ctx.rotate(-Math.PI / 2);
      pill(ctx, 0, 0, fmt(h + fw * 2), c);
      ctx.restore();
    }
    ctx.restore();
  };

  function line(ctx, x1, y1, x2, y2) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  function tick(ctx, x, y, vertical) {
    ctx.beginPath();
    if (vertical) {
      ctx.moveTo(x, y - 4);
      ctx.lineTo(x, y + 4);
    } else {
      ctx.moveTo(x - 4, y);
      ctx.lineTo(x + 4, y);
    }
    ctx.stroke();
  }
  function pill(ctx, x, y, text, c) {
    var tw = ctx.measureText(text).width + 14;
    ctx.save();
    ctx.fillStyle = c.labelBg || '#FFFFFF';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x - tw / 2, y - 10, tw, 20, 10);
    else ctx.rect(x - tw / 2, y - 10, tw, 20);
    ctx.fill();
    ctx.restore();
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y + 0.5);
  }

  function shade(hex, amount) {
    var m = String(hex).replace('#', '');
    if (m.length === 3) m = m[0] + m[0] + m[1] + m[1] + m[2] + m[2];
    var n = parseInt(m, 16);
    if (isNaN(n)) return hex;
    var r = (n >> 16) & 255;
    var g = (n >> 8) & 255;
    var b = n & 255;
    var f = function (v) {
      return Math.round(amount >= 0 ? v + (255 - v) * amount : v * (1 + amount));
    };
    return 'rgb(' + f(r) + ',' + f(g) + ',' + f(b) + ')';
  }

  // Canvas px → inches from the mirror centre (for dragging the design).
  Mirror.prototype.toInches = function (clientX, clientY) {
    var rect = this.canvas.getBoundingClientRect();
    var L = this.layout(this.cssW, this.cssH, this.state.dims);
    return { x: (clientX - rect.left - L.cx) / L.k, y: (clientY - rect.top - L.cy) / L.k };
  };
  Mirror.prototype.pxPerInch = function () {
    return this.layout(this.cssW, this.cssH, this.state.dims).k;
  };

  // A clean picture of the mirror for the order (no labels, no guides), as a PNG blob.
  Mirror.prototype.toBlob = function (size, cb) {
    var c = document.createElement('canvas');
    var ratio = this.state.h / this.state.w;
    c.width = size;
    c.height = Math.round(size * Math.max(ratio, 0.5));
    c.dataset.wall = '1';
    this.draw(c);
    try {
      c.toBlob(cb, 'image/png');
    } catch (e) {
      cb(null);
    }
  };

  // The print layer alone over transparency, laid over the bounding box of the shape: the 3D texture.
  Mirror.prototype.printTexture = function (px) {
    var s = this.state;
    var c = this.texCanvas || (this.texCanvas = document.createElement('canvas'));
    var aspect = s.h / s.w;
    c.width = px;
    c.height = Math.round(px * aspect);
    var ctx = c.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    var k = c.width / s.w;
    var pts = outline(s.kind, s.w, s.h, s.r);
    this.paintPrint(ctx, k, c.width / 2, c.height / 2, pts, s.w, s.h, { clean: true });
    return c;
  };

  Mirror.prototype.destroy = function () {
    if (this.ro) this.ro.disconnect();
    if (this.anim) cancelAnimationFrame(this.anim);
  };

  window.GHMirror = Mirror;
})();
